import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDocumentManagerCapability } from '@embedpdf/plugin-document-manager/react'
import { Icon } from '../ui/icon'
import { getAllDocuments, saveDocument, getPreference, setPreference } from '../../services/indexeddb-service'
import { OcrService } from '../../services/ocr/ocr-service'
import type { DocRecord } from '../../services/indexeddb-service'
import type { OcrHistoryEntry } from '../../services/ocr/ocr-types'

function formatDate(ts: number): string {
  const d = new Date(ts)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHrs = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHrs < 24) return `${diffHrs}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return d.toLocaleDateString()
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function snippet(text: string, maxLen = 120): string {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (cleaned.length <= maxLen) return cleaned
  return cleaned.slice(0, maxLen) + '...'
}

export const Dashboard = () => {
  const { t, i18n } = useTranslation()
  const { provides: docManager } = useDocumentManagerCapability()
  const [recentDocs, setRecentDocs] = useState<DocRecord[]>([])
  const [ocrHistory, setOcrHistory] = useState<OcrHistoryEntry[]>(OcrService.history)
  const [loading, setLoading] = useState(true)
  const [isDragOver, setIsDragOver] = useState(false)
  const urlInputRef = useRef<HTMLInputElement>(null)

  const loadDocs = useCallback(() => {
    getAllDocuments().then((docs) => {
      setRecentDocs(docs.filter(d => d.id !== 'current-document'))
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    loadDocs()
    const unsub = OcrService.subscribe(() => {
      setOcrHistory([...OcrService.history])
    })
    return unsub
  }, [loadDocs])

  const handleOpenDocument = useCallback(async (doc: DocRecord) => {
    if (!docManager) return
    const task = docManager.openDocumentBuffer({
      name: doc.name,
      buffer: doc.buffer.slice(0),
      autoActivate: true,
    })
    task.wait(async () => {
      OcrService.setDocumentName(doc.name)
      const record = { ...doc, lastOpened: Date.now() }
      saveDocument(record).then(loadDocs)
      saveDocument({ ...record, id: 'current-document' }).catch(() => {})
      const list = (await getPreference<string[]>('open-documents')) || []
      if (!list.includes(doc.name)) {
        await setPreference('open-documents', [...list, doc.name])
      }
    }, () => {})
  }, [docManager, loadDocs])

  const handleOpenFilePicker = useCallback(() => {
    document.getElementById('dashboard-file-input')?.click()
  }, [])

  const handleOpenFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file || !docManager) return
    const buffer = await file.arrayBuffer()
    docManager.openDocumentBuffer({
      name: file.name,
      buffer,
      autoActivate: true,
    })
    if (e.currentTarget) e.currentTarget.value = ''
    OcrService.setDocumentName(file.name)
    saveDocument({
      id: file.name,
      name: file.name,
      buffer: buffer.slice(0),
      byteLength: buffer.byteLength,
      currentPage: 0,
      lastOpened: Date.now(),
    }).catch(() => {})
    getPreference<string[]>('open-documents').then(list => {
      setPreference('open-documents', [...(list || []), file.name])
    }).catch(() => {})
  }, [docManager])

  const handleOpenUrl = useCallback(async () => {
    const url = urlInputRef.current?.value.trim()
    if (!url || !docManager) return
    const name = url.split('/').pop()?.split('?')[0] || 'document.pdf'
    const displayName = name.endsWith('.pdf') ? name : `${name}.pdf`

    try {
      const proxyUrl = `https://opakorpdf.rakzmey.workers.dev/proxy?url=${encodeURIComponent(url)}`
      const res = await fetch(proxyUrl)
      if (!res.ok) throw new Error('Failed to fetch')
      const buffer = await res.arrayBuffer()
      docManager.openDocumentBuffer({
        name: displayName,
        buffer,
        autoActivate: true,
      })
    } catch {
      docManager.openDocumentUrl({
        url,
        name: displayName,
        autoActivate: true,
      })
    }
    if (urlInputRef.current) urlInputRef.current.value = ''
  }, [docManager])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (!file || !docManager) return
    const reader = new FileReader()
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) return
      docManager.openDocumentBuffer({
        name: file.name,
        buffer: reader.result,
        autoActivate: true,
      })
    }
    reader.readAsArrayBuffer(file)
  }, [docManager])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng)
    localStorage.setItem('opakor_lang', lng)
  }, [i18n])

  return (
    <div className="flex-1 flex flex-col overflow-auto pt-16" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      <div className="w-full max-w-2xl mx-auto px-6 flex flex-col items-center gap-8 flex-shrink-0">
        <div className="flex items-center rounded-full border border-border-default bg-bg-app p-1 self-end">
          <button
            onClick={() => changeLanguage('km')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('km') ? 'bg-bg-surface text-fg-primary' : 'text-fg-secondary hover:text-fg-primary'}`}
          >
            ភាសាខ្មែរ
          </button>
          <button
            onClick={() => changeLanguage('en')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-bg-surface text-fg-primary' : 'text-fg-secondary hover:text-fg-primary'}`}
          >
            English
          </button>
        </div>

        <div className="text-center flex flex-col items-center gap-3">
          <div className="w-20 h-20 rounded-2xl bg-bg-surface flex items-center justify-center border border-border-default shadow-lg overflow-hidden">
            <img src="/pwa-icon.svg" alt="OpakorPDF" className="w-12 h-12" />
          </div>
          <div>
            <p className="text-fg-primary font-semibold text-lg">{t('app.welcome')}</p>
            <p className="text-fg-muted text-sm mt-1">{t('app.welcomeHint')}</p>
          </div>
          <div className={`w-full max-w-md rounded-2xl border-2 border-dashed p-6 flex flex-col items-center gap-4 transition-all duration-200 ${isDragOver ? 'border-accent bg-accent/5 scale-[1.02] shadow-lg shadow-accent/10' : 'border-border-default/60 bg-bg-surface/50'}`}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="12" y2="12" />
                <line x1="15" y1="15" x2="12" y2="12" />
              </svg>
            </div>
            <div className="text-center">
              <button
                onClick={handleOpenFilePicker}
                className="px-6 py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                {t('app.openFile')}
              </button>
              <p className="text-xs text-fg-muted mt-2">{t('app.dropHint')}</p>
            </div>
          </div>

          <div className="w-full max-w-md flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-bg-surface rounded-lg border border-border-default px-3 h-10">
              <Icon icon="link" size={14} className="text-fg-muted shrink-0" />
              <input
                ref={urlInputRef}
                type="url"
                placeholder={t('app.openUrlPlaceholder')}
                className="flex-1 bg-transparent text-sm text-fg-primary outline-none placeholder:text-fg-muted/50"
                onKeyDown={(e) => { if (e.key === 'Enter') handleOpenUrl() }}
              />
            </div>
            <button
              onClick={handleOpenUrl}
              className="px-4 h-10 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 transition-colors shrink-0"
            >
              {t('app.openUrl')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-bg-surface rounded-xl border border-border-default/50 p-4">
            <h3 className="text-sm font-semibold text-fg-primary mb-3 flex items-center gap-2">
              <Icon icon="file" size={16} />
              {t('dashboard.recentDocuments')}
            </h3>
            {loading ? (
              <div className="flex items-center gap-2 text-fg-muted text-sm">
                <div className="w-4 h-4 border-2 border-fg-muted/30 border-t-fg-muted rounded-full animate-spin" />
                Loading...
              </div>
            ) : recentDocs.length === 0 ? (
              <p className="text-xs text-fg-muted leading-relaxed">{t('dashboard.recentDocumentsEmpty')}</p>
            ) : (
              <div className="flex flex-col gap-1">
                {recentDocs.slice(0, 6).map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => handleOpenDocument(doc)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-bg-elevated/50 transition-colors text-left group"
                  >
                    <Icon icon="file" size={16} className="text-fg-muted group-hover:text-accent transition-colors shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-fg-primary truncate">{doc.name}</p>
                      <p className="text-[10px] text-fg-muted">
                        {formatSize(doc.byteLength)} &middot; {formatDate(doc.lastOpened)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-bg-surface rounded-xl border border-border-default/50 p-4">
            <h3 className="text-sm font-semibold text-fg-primary mb-3 flex items-center gap-2">
              <Icon icon="scanText" size={16} />
              {t('dashboard.ocrHistory')}
            </h3>
            {ocrHistory.length === 0 ? (
              <p className="text-xs text-fg-muted leading-relaxed">{t('dashboard.ocrHistoryEmpty')}</p>
            ) : (
              <div className="flex flex-col gap-1">
                {ocrHistory.slice(0, 6).map((entry) => (
                  <div
                    key={entry.id}
                    className="px-3 py-2 rounded-lg hover:bg-bg-elevated/50 transition-colors"
                  >
                    <p className="text-xs text-fg-primary line-clamp-2 leading-relaxed">{snippet(entry.text)}</p>
                    <p className="text-[10px] text-fg-muted mt-1">
                      {t('dashboard.page')} {entry.pageIndex + 1} &middot; {entry.lineCount} {t('dashboard.lines')} &middot; {formatDate(entry.timestamp)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <input
        id="dashboard-file-input"
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleOpenFile}
      />
    </div>
  )
}
